import {CONSENT_STORAGE_KEY} from '../consent/cookie-consent.service';
import { Injectable, inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import {LoginError} from '../enums/login-error.enum';
import {CacheCleanerInterface} from '../interface/cache-cleaner.interface';
import {AsyncLoaderInterface} from '../interface/async-loader.interface';
import {FirebaseApi} from './firebase-api.service';
import {pickUserFields, USER_PROFILE_FIELDS, USER_SIGNIN_FIELDS, UserInfo} from '../model/user-info';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {Logger} from '../utils/logger';
import {ObjectUtil} from '../utils/object.util';
import {UserSubscription} from '../model/user-subscription';
import {PaymentGateway} from '../model/payment-gateway.enum';
import {SubscriptionPlan} from '../enums/subscription-plan.enum';
import {SubscriptionState} from '../model/subscription-state.enum';
import {CommonUtil} from '../utils/common.util';
import firebase from 'firebase/compat/app';
import {getAuth, getMultiFactorResolver, TotpMultiFactorGenerator} from 'firebase/auth';
import {SubscriptionApiService} from './api/routes/subscription-api.service';
import {SettingsService} from '../../canvas/services/settings.service';
import {UserApiService} from './api/routes/user-api.service';
import {AuthApiService} from './api/routes/auth-api.service';
import {AccountApiService} from './api/routes/account-api.service';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {StringsService} from '../../../../projects/common/src/lib/services/strings.service';
import {Language} from '../../../../projects/common/src/lib/enums/language.enum';
import {EnterpriseRole} from "../enums/enterprise-role";
import {DEVICE_SESSION_STORAGE_KEY, DeviceSessionService} from './device-session.service';
import UserCredential = firebase.auth.UserCredential;
import FirebaseUser = firebase.User;

@Injectable({
  providedIn: 'root',
})
export class AuthService implements AsyncLoaderInterface, CacheCleanerInterface {
  private firebaseAuth = inject(AngularFireAuth);
  private firestore = inject(AngularFirestore);
  private firebaseApi = inject(FirebaseApi);
  private usersApi = inject(UserApiService);
  private subscriptionApi = inject(SubscriptionApiService);
  private settingsService = inject(SettingsService);
  private stringsService = inject(StringsService);
  private authApi = inject(AuthApiService);
  private accountApi = inject(AccountApiService);
  private injector = inject(EnvironmentInjector);

  public userInfoSubject = new BehaviorSubject<UserInfo>(null);
  public subscriptionSubject = new BehaviorSubject<UserSubscription>(null);
  public enterpriseSubject = new BehaviorSubject<UserInfo>(null);
  private userCredential: UserCredential = null;
  private user: FirebaseUser;
  private userInfo: UserInfo;
  private subscriptionSubscription: Subscription;
  /** Whose subscription document the listener is on. */
  private subscriptionListenerUid: string | null = null;
  private userInfoSubscription: Subscription;
  private fireAuthSubscription: Subscription;

  private trialSubscription = {
    created_on: Date.now(),
    updated_on: Date.now(),
    period_start: Date.now(),
    expires_on: Date.now(),
    period_end: 1573426319000,
    payment_gateway: PaymentGateway.TRIAL,
    plan_id: SubscriptionPlan.TRIAL,
    state: SubscriptionState.TRIALING,
    uid: ''
  };

  get currentUser(): FirebaseUser {
    return this.user;
  }

  get getUserId(): string {
    return this.user ? this.user.uid : null;
  }

  get currentUserInfo(): UserInfo {
    return this.userInfo;
  }

  get currentUserObservable(): Observable<FirebaseUser> {
    return this.firebaseAuth.authState;
  }

  load() {

    Logger.log('AuthService load 1');
    return new Promise((resolve, reject) => {
      Logger.log('AuthService load 2');
      if (this.fireAuthSubscription) return;
      Logger.log('AuthService load 3');
      this.fireAuthSubscription = this.firebaseAuth.authState.subscribe(async user => {

        this.subscriptionSubject.next(null);
        let isGod = false;
        if (user) {
          try {
            const claims = (await user.getIdTokenResult(true)).claims;
            isGod = claims[`accountRole`] === 'admin';
            Logger.isGod = isGod;
            Logger.log('CLAIMS ', claims);
          } catch (e) {
            Logger.error(e);
          }
        }
        this.user = user;
        Logger.log('authStateChanged 11 ', user);
        if (user && !user.emailVerified) {
          resolve(false);
        } else {
          Logger.log('authStateChanged 2222 ');
          if (user && !user.isAnonymous) {
            Logger.log('authStateChanged 3333');
            const userRef = runInInjectionContext(this.injector, () => this.firebaseApi.getCurrentUserRef());
            this.userInfoSubscription = userRef.valueChanges().subscribe(value => {
              Logger.log('onUserInfoChanged 111:', value);
              // The partner flag too: the Clients page and the /billing guard read it.
              if (value && this.userInfo && ((value as any).enterpriseRole !== this.userInfo.enterpriseRole
                || !!(value as any).partner?.active !== !!this.userInfo.partner?.active)) {
                this.refreshClaimsSoon();
              }
              if (value) {
                this.userInfo = ObjectUtil.identity(value, new UserInfo());
                // The two bookkeeping writes below normally echo back through
                // this listener, which then lands here. If one is refused
                // (Firestore rules) that echo never comes, so carry on
                // without it rather than leave the app on its splash screen.
                const proceed = () => this.onUserInfoReady(user, isGod, resolve);
                if (!this.userInfo.billingInfo) {
                  Logger.log('onUserInfoChanged Creating Billing Info');
                  this.userInfo.billingInfo = {
                    name: this.userInfo.taxId ? this.userInfo.taxId.business : this.userInfo.fullName,
                    // Left blank on purpose: an empty billing email means
                    // "use the sign-in address", and seeding a copy here would
                    // freeze today's address into billing, so a later email
                    // change would silently stop moving the invoices.
                    email: '',
                    address: this.userInfo.address,
                    zipCode: this.userInfo.zipCode,
                    country: this.userInfo.country,
                    phoneNumber: this.userInfo.phoneNumber,
                    taxId: this.userInfo.taxId ? this.userInfo.taxId : {
                      business: '',
                      value: '',
                      type: ''
                    }
                  }

                  this.updateUserInfo({
                    billingInfo: this.userInfo.billingInfo
                  }).catch(err => {
                    Logger.error('Seeding billing details failed', err);
                    proceed();
                  });
                } else if (!this.userInfo.lastSignin || Date.now() - this.userInfo.lastSignin > 1800000) {
                  Logger.log('onUserInfoChanged Updating user timestamp');
                  const updateData: any = {
                    lastSignin: Date.now(),
                    emailVerified: user.emailVerified
                  };
                  // Set by the server at sign-up; only filled in where missing
                  // (the rules refuse to rewrite an account's creation date).
                  if (!value.createdOn) {
                    updateData.createdOn = Date.parse(this.user.metadata.creationTime);
                  }
                  const country = this.userInfo.country;
                  if (country && typeof country !== 'string') {
                    updateData.country = '';
                  }
                  this.stampSignIn(updateData).catch(err => {
                    Logger.error('Sign-in stamp failed', err);
                    proceed();
                  });
                } else {
                  proceed();
                }

              } else {
                Logger.error('USER INFO NOT FOUND!!!');
                // this.setupNewUser(user, user.displayName, '');
              }
            });
          } else if (user && user.isAnonymous) {
            Logger.log('Anonymous user here!!');
            this.init();
            resolve(true);
          } else if (!user) {
            Logger.log('Anonymous user here 22!!');
            if (CommonUtil.inIframe()) {
              Logger.log('Signing in anonymously...');
              this.firebaseAuth.signInAnonymously().then(value => {
                Logger.log('Signed In Anonymously');
                // resolve(true);

                this.init()
                resolve(true);
              });
            } else {
              Logger.error('Auth destroy');
              this.destroy();
              this.init();
              resolve(false);
            }
          }
        }
      });
    });
  }

  /** The user document is current: start everything that depends on it. */
  private onUserInfoReady(user: FirebaseUser, isGod: boolean, resolve: (value: boolean) => void) {
    this.userInfo._admin = isGod;

    Logger.log('onUserInfoChanged starting subscription listener', this.userInfo);
    if (this.userInfo.enterpriseUid
      && this.userInfo.enterpriseUid.trim().length > 0
      && this.userInfo.enterpriseRole === EnterpriseRole.ADMIN) {
      this.startSubscriptionListener(this.userInfo.enterpriseUid);
    } else {
      this.startSubscriptionListener(user.uid);
    }

    this.userInfoSubject.next(this.userInfo);
    Logger.log('onUserInfoChanged Init Settings', this.userInfo);
    this.init();
    resolve(true);
  }

  init() {
    this.settingsService.initCanvasSettings();

    // Here rather than in afterLogin: this runs for a returning visitor whose
    // token is still valid too, and they never pass through the sign-in path.
    // Anonymous users are skipped — there is no account to list devices for.
    if (this.user && !this.user.isAnonymous) {
      this.deviceSessions.start();
    }

    // Members no longer read their organization owner's user document: it
    // carries the owner's billing details and Stripe ids, and nothing on the
    // member side used it. What a member shows of the organization comes from
    // `enterpriseBrand` on their own document (stamped by the server), and
    // colleagues from GET /directory/organization. `enterpriseSubject` stays
    // for API compatibility and remains null.
    if (this.userInfo) {
      let lang = Language[this.userInfo.language.toUpperCase()];
      if (!lang) {
        lang = this.settingsService.getLocalValue('language', navigator.language);
      }
      Logger.log('Setting language to ', lang);
      if (lang && Language[lang.toUpperCase()]) {
        Logger.log('Setting language to 2 ', lang);
        this.settingsService.setLocalValue('language', lang);
        this.stringsService.setLanguage(lang);
      }
    }
  }

  isLoggedIn(): boolean {
    return !!this.user;
  }

  public signInWithProvider(provider: firebase.auth.AuthProvider, company: string = ''): Promise<UserCredential> {
    return new Promise<UserCredential>((resolve, reject) => {
      this.firebaseAuth.signInWithPopup(provider)
        .then(async userCredential => {
          const user = userCredential.user;
          await this.afterLogin(userCredential, user.displayName, company);
          resolve(userCredential);
        })
        .catch(err => {
          Logger.error(err);
          this.userCredential = null;
          this.user = null;
          reject(this.handleError(err));
        });
    });
  }

  public loginWithGoogle(): Promise<UserCredential> {
    return this.signInWithProvider(new firebase.auth.GoogleAuthProvider())
  }

  public loginWithEmail(email: string, password: string): Promise<UserCredential> {
    return new Promise<UserCredential>((resolve, reject) => {
      this.firebaseAuth.signInWithEmailAndPassword(email, password)
        .then(async userCredential => {
          const user = userCredential.user;
          await this.afterLogin(userCredential, user.displayName, '');
          resolve(userCredential);
        })
        .catch(err => {
          this.userCredential = null;
          this.user = null;
          reject(this.handleError(err));
        });
    });
  }

  public loginWithCustomToken(token: string): Promise<UserCredential> {
    return new Promise<UserCredential>((resolve, reject) => {
      this.firebaseAuth.signInWithCustomToken(token)
        .then(async userCredential => {
          const user = userCredential.user;
          await this.afterLogin(userCredential, user.displayName, '');
          resolve(userCredential);
        })
        .catch(err => {
          this.userCredential = null;
          this.user = null;
          reject(this.handleError(err));
        });
    });
  }

  public registerWithEmail(fullName: string,
                           company: string,
                           email: string,
                           password: string): Promise<UserCredential> {
    return new Promise<UserCredential>((resolve, reject) => {
      this.firebaseAuth.createUserWithEmailAndPassword(email, password)
        .then(async userCredential => {
          await this.afterLogin(userCredential, fullName, company);
          resolve(userCredential);
        })
        .catch(err => {
          this.userCredential = null;
          this.user = null;
          reject(this.handleError(err));
        });
    });
  }

  private async afterLogin(userCredential: UserCredential,
                           fullName: string = userCredential.user.displayName,
                           company: string = '') {
    this.userCredential = userCredential;
    const user = userCredential.user;
    this.user = user;

    this.firebaseApi.currentUser = userCredential.user;
    let userInfo = null;
    try {
      userInfo = await this.firebaseApi.getUserById(userCredential.user.uid);
    } catch (e) {
    }
    // Optional-chained: a credential rebuilt after a second-factor challenge
    // carries no additionalUserInfo, and reading .isNewUser off undefined here
    // would throw after the password and the code had both been accepted.
    const isNewUser = !!this.userCredential.additionalUserInfo?.isNewUser;
    Logger.log('afterLogin: ', this.userCredential.user.uid, isNewUser, !userInfo)
    if (isNewUser || !userInfo) {
      await this.setupNewUser(user, fullName, company);
    }
    return userCredential;
  }

  /** Our own reset email, in the account's language (see AccountApiService). */
  public sendPasswordResetEmail(email: string) {
    return this.accountApi.requestPasswordReset((email || '').trim(), this.stringsService.getLanguage());
  }

  // ── Second factor at sign-in ─────────────────────────────────────
  // Enrolment lives in AccountSecurityService; this is the other half. Without
  // it, enrolling a second factor locks the account out: the sign-in throws
  // `auth/multi-factor-auth-required` and there is nowhere to type the code.
  //
  // The resolver hangs off the RAW Firebase error, which handleError otherwise
  // flattens into a LoginError, so it is kept here for the challenge step.

  private pendingMfaError: any = null;

  get hasPendingMfaChallenge(): boolean {
    return !!this.pendingMfaError;
  }

  /** What the user enrolled, for the prompt ("Authenticator app"). */
  get pendingMfaFactorName(): string {
    if (!this.pendingMfaError) { return ''; }
    try {
      const resolver = getMultiFactorResolver(getAuth(), this.pendingMfaError);
      return resolver.hints?.[0]?.displayName ?? '';
    } catch {
      return '';
    }
  }

  /** Abandon a half-finished sign-in (user backed out of the code prompt). */
  cancelMfaChallenge() {
    this.pendingMfaError = null;
  }

  /**
   * Finish a sign-in that stopped for a second factor.
   *
   * `resolveSignIn` returns a MODULAR credential, which has no
   * `additionalUserInfo` — the field afterLogin reads to decide whether to
   * provision a new account. Anyone completing a second-factor challenge
   * necessarily has an account already, so that branch is skipped explicitly
   * rather than left to read undefined.
   */
  async completeTotpSignIn(code: string): Promise<void> {
    if (!this.pendingMfaError) {
      throw new Error('No second-factor challenge in progress');
    }
    const resolver = getMultiFactorResolver(getAuth(), this.pendingMfaError);
    const hint = resolver.hints.find(h => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
    if (!hint) {
      throw new Error('No authenticator-app factor on this account');
    }
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, (code || '').trim());
    const modularCredential = await resolver.resolveSignIn(assertion);
    this.pendingMfaError = null;

    // Prefer the user compat reports, since that is the object the rest of the
    // app holds; both wrap the same auth instance. Falling back to the modular
    // credential rather than trusting compat to have caught up — arriving here
    // with no user would throw AFTER the password and the code were accepted,
    // which is the worst possible moment to fail.
    const user = (await this.firebaseAuth.currentUser) ?? (modularCredential?.user as any);
    if (!user) {
      throw new Error('Signed in with a second factor but no user was returned');
    }
    await this.afterLogin({user, additionalUserInfo: {isNewUser: false}} as any);
  }

  handleError(err: any) {
    Logger.error(err);
    // Keep the raw error: the multi-factor resolver is attached to it and
    // cannot be rebuilt from a flattened enum value.
    if (err?.code === 'auth/multi-factor-auth-required') {
      this.pendingMfaError = err;
      return LoginError.MFA_REQUIRED;
    }
    switch (err?.code) {
      // With email enumeration protection on (Firebase's default since 2023)
      // a wrong password, and an address with no account, both come back as
      // `invalid-credential`. It fell through to "unexpected error, contact
      // us" -- what anyone with a typo in their password was told.
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
      case 'auth/invalid-login-credentials':
      case 'auth/missing-password':
        return LoginError.INVALID_PASSWORD;
      case 'auth/invalid-email':
        return LoginError.INVALID_EMAIL;
      case 'auth/user-not-found':
        return LoginError.USER_NOT_FOUND;
      case 'auth/user-disabled':
        return LoginError.USER_DISABLED;
      case 'auth/network-request-failed':
        return LoginError.NETWORK_REQUEST_FAILED;
      case 'auth/too-many-requests':
        return LoginError.TOO_MANY_REQUESTS;

      //  for Register with email/pass
      case 'auth/email-already-in-use':
        return LoginError.EMAIL_ALREADY_IN_USE;
      case 'auth/operation-not-allowed':
        return LoginError.OPERATION_NOT_ALLOWED;
      case 'auth/weak-password':
      case 'auth/password-does-not-meet-requirements':
        return LoginError.WEAK_PASSWORD;

      //  For signin with popup
      case 'auth/account-exists-with-different-credential':
        return LoginError.EXISTS_WITH_DIFFERENT_CREDENTIAL;
      // Closing the Google or company window is a change of mind, not an
      // error; it used to raise "unexpected error, contact us".
      case 'auth/popup-closed-by-user':
      case 'auth/cancelled-popup-request':
      case 'auth/user-cancelled':
        return LoginError.POPUP_CLOSED;
      case 'auth/popup-blocked':
        return LoginError.POPUP_BLOCKED;

      default:
        return LoginError.UNKNOWN;
    }
  }

  getIdToken(noRefresh: boolean = false) {
    if (this.user === null) {
      return null;
    }

    return this.user.getIdToken(!noRefresh);
  }

  public loadServiceData(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.firebaseAuth.authState.subscribe(user => {
        if (user) {
          this.user = user;
          return resolve();
        }

        return reject();
      });
    });
  }

  public clearCachedData(): Promise<boolean> {
    return this.signOut();
  }

  isGod(): boolean {
    if (this.userInfo) {
      return this.userInfo._admin && this.settingsService.local.adminMode;
    }
    return false;
  }

  async signOut() {
    // Before the Firebase sign-out: it is the last moment this device can
    // still prove who it is.
    await this.deviceSessions.end();
    await this.firestore.firestore.terminate();
    await this.firestore.firestore.clearPersistence();
    await this.firebaseApi.signOut();
    // The device id has to survive the wipe. It identifies the BROWSER, not
    // the session — clearing it would make every sign-out look like a brand
    // new device, and the security page would fill up with duplicate entries
    // for the same laptop, which is exactly the noise that list exists to cut
    // through.
    const deviceId = localStorage.getItem(DEVICE_SESSION_STORAGE_KEY);
    // The cookie choice belongs to the browser too: wiping it asked the same
    // person again after every sign-out.
    const cookieChoice = localStorage.getItem(CONSENT_STORAGE_KEY);
    // So do the theme and the language (the cookie catalogue's "appearance"):
    // a dark-theme user signed out to a light sign-in page, and stayed light
    // until they switched again. The colour theme too (appTheme).
    const appearance = ['theme', 'themeMode', 'appTheme', 'language']
      .map(key => [key, localStorage.getItem(key)] as [string, string | null]);
    localStorage.clear();
    if (deviceId) {
      localStorage.setItem(DEVICE_SESSION_STORAGE_KEY, deviceId);
    }
    if (cookieChoice) {
      localStorage.setItem(CONSENT_STORAGE_KEY, cookieChoice);
    }
    appearance.forEach(([key, value]) => {
      if (value !== null) {
        localStorage.setItem(key, value);
      }
    });
    this.destroy();
    return true;
  }

  /**
   * Resolved lazily: DeviceSessionService needs AuthService to sign a revoked
   * device out, so injecting it directly here would be a cycle.
   */
  private get deviceSessions(): DeviceSessionService {
    return this.injector.get(DeviceSessionService);
  }

  /**
   * Saves the signed-in user's own profile fields.
   *
   * Callers often pass the whole UserInfo (profile and billing pages save a
   * clone of it), which includes server-owned fields -- enterpriseUid,
   * stripe ids, the locally computed `_admin`, defaults the document never
   * had. Only USER_PROFILE_FIELDS are sent: the Firestore rules refuse any
   * write that changes anything else, and would otherwise refuse the whole
   * save.
   */
  updateUserInfo(data: any) {
    return this.writeOwnUserFields(data, USER_PROFILE_FIELDS);
  }

  /** The sign-in bookkeeping (last sign-in, verified flag, creation date). */
  private stampSignIn(data: any) {
    return this.writeOwnUserFields(data, [...USER_PROFILE_FIELDS, ...USER_SIGNIN_FIELDS]);
  }

  private writeOwnUserFields(data: any, fields: readonly string[]) {
    const uid = this.getUserId;
    if (!uid) {
      return Promise.reject(403);
    }
    const allowed = pickUserFields(data, fields);
    Logger.log('updateUserInfo ', allowed);
    if (Object.keys(allowed).length === 0) {
      return Promise.resolve();
    }
    return this.firebaseApi.updateUserInfo(uid, allowed);
  }

  isRealUser() {
    return this.user && !this.user.isAnonymous;
  }

  isAnonymousUser() {
    return this.user && this.user.isAnonymous;
  }

  private lastPlanId: string = null;
  private lastManagedBy: string = null;

  /**
   * The route guards read the plan and the org role from the sign-in token,
   * whose claims the server rewrites only after the change -- the token in
   * hand still has the old ones, so a new Enterprise owner or a promoted ADMIN
   * was turned away from pages they had just gained until the token renewed
   * itself, up to an hour later. Asked for again once the server has had time.
   */
  private refreshClaimsSoon() {
    [3000, 10000].forEach(ms => setTimeout(() => {
      if (this.user) {
        this.user.getIdToken(true).catch(err => Logger.error('Token refresh failed', err));
      }
    }, ms));
  }

  private startSubscriptionListener(uid: string) {
    // Every update of the user's document comes through here; restarting the
    // listener each time re-read the subscription for nothing.
    if (uid && uid === this.subscriptionListenerUid && this.subscriptionSubscription
      && !this.subscriptionSubscription.closed) {
      return;
    }
    this.subscriptionListenerUid = uid || null;
    if (uid) {
      const mySubRef = this.firebaseApi.getSubscriptionDoc(uid);
      if (this.subscriptionSubscription) {
        this.subscriptionSubscription.unsubscribe();
      }
      this.subscriptionSubscription = mySubRef.valueChanges().subscribe(subscription => {
        if (subscription) {
          Logger.log('Subscription Found', subscription);
          // A partner taking over or letting go (managed_by) too: the /billing
          // guard turned an ex-client away from choosing a plan of its own.
          if ((this.lastPlanId !== null && this.lastPlanId !== subscription.plan_id)
            || (this.lastManagedBy !== null && this.lastManagedBy !== (subscription.managed_by || ''))) {
            this.refreshClaimsSoon();
          }
          this.lastPlanId = subscription.plan_id;
          this.lastManagedBy = subscription.managed_by || '';
          this.subscriptionSubject.next(subscription);
        } else {
          Logger.error('SUBSCRIPTION NOT FOUND');
          this.subscriptionApi.subscribeTrial();
        }
      });
    }
  }

  private destroy() {
    this.user = null;
    this.userInfo = null;
    this.userCredential = null;
    this.userInfoSubject.next(null);
    if (this.subscriptionSubscription) {
      this.subscriptionSubscription.unsubscribe();
    }
    this.subscriptionListenerUid = null;
    if (this.userInfoSubscription) {
      this.userInfoSubscription.unsubscribe();
    }
  }

  private setupNewUser(user: FirebaseUser,
                       fullName: string,
                       company: string) {
    const newData: { fullName: string, company?: string } = {
      fullName
    };
    if (company) {
      newData.company = company;
    }
    return this.usersApi.createNewUser(newData);
  }

  get isEnterpriseUser(): boolean {
    return this.userInfo && !!this.userInfo.enterpriseUid;
  }

  get hasEnterpriseSubscription(): boolean {
    const sub = this.subscriptionSubject.value;
    return sub && (sub.plan_id === SubscriptionPlan.ENTERPRISE_MONTHLY || sub.plan_id === SubscriptionPlan.ENTERPRISE_YEARLY);
  }

  get hasOpenGuestSubscription(): boolean {
    const sub = this.subscriptionSubject.value;
    // Open Guest is usage-metered and sold as MONTHLY ONLY by design —
    // OPEN_GUEST_YEARLY exists in the enum but is not an active plan.
    return sub && sub.plan_id === SubscriptionPlan.OPEN_GUEST_MONTHLY;
  }

  get hasProSubscription(): boolean {
    const sub = this.subscriptionSubject.value;
    return sub && (sub.plan_id === SubscriptionPlan.PRO_MONTHLY || sub.plan_id === SubscriptionPlan.PRO_YEARLY);
  }

  get hasDeveloperSubscription(): boolean {
    return this.hasOpenGuestSubscription || this.hasEnterpriseSubscription;
  }

  get hasReachedMaxEnterpriseUsers(): boolean {
    const sub = this.subscriptionSubject.value;
    // enterpriseUsers may be missing on user docs (e.g. member-admins) —
    // never throw from a template-bound getter.
    const seatCount = this.userInfo?.enterpriseUsers?.length ?? 0;
    if (this.hasActiveEnterprisePlan() && seatCount >= sub.quantity) {
      return true;
    }
    return false;
  }

  /**
   * The organisation whose members this account manages: its own, or its
   * owner's when it is that organisation's ADMIN. The Enterprise page listed
   * the ADMIN's own (empty) set of members.
   */
  get managedOrganizationUid(): string {
    const info = this.userInfo;
    if (info && info.enterpriseUid && info.enterpriseRole === EnterpriseRole.ADMIN) {
      return info.enterpriseUid;
    }
    return this.getUserId;
  }

  get isAllowedToCreateEnterpriseUser(): boolean {
    const sub = this.subscriptionSubject.value;
    const seatCount = this.userInfo?.enterpriseUsers?.length ?? 0;
    if (this.hasActiveEnterprisePlan() && (seatCount < sub.quantity || sub.autoincrement_quantity)) {
      return true;
    }
    return false;
  }

  get getEnterpriseActiveUsersCount() {
    return this.userInfo.enterpriseUsers ? this.userInfo.enterpriseUsers.length : 0;
  }

  hasActiveEnterprisePlan(): boolean {
    const sub = this.subscriptionSubject.value;
    if (!sub) {
      return false;
    }
    let expiresOn = sub.expires_on
    if (!expiresOn || Number.isNaN(expiresOn) || isNaN(expiresOn)) {
      expiresOn = sub.period_end
    }
    return sub
      && expiresOn > new Date().getTime()
      && (sub.plan_id === SubscriptionPlan.ENTERPRISE_YEARLY || sub.plan_id === SubscriptionPlan.ENTERPRISE_MONTHLY)
      && sub.state !== SubscriptionState.PAST_DUE
      && sub.state !== SubscriptionState.EXPIRED
      && sub.state !== SubscriptionState.UNPAID
      && sub.state !== SubscriptionState.INCOMPLETE;
  }

  createCustomToken() {
    return this.authApi.createCustomToken();
  }
}
